import { View, StyleSheet, Text, Image } from '@react-pdf/renderer';
import { toPairs } from 'lodash';
import { range2Value, value2Range, getLabels } from 'utilities/color';

const styles = StyleSheet.create({
  scale: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: '5',
  },
  label: {
    width: 'auto',
    paddingTop: '2'
  },
  image: {
    height: '10',
    width: '350',
    marginBottom: '2',
    borderRadius: '2'
  }
});

/**
* @function getImage
* @description Helper function that generates gradient scale image
* @param {Object} plotProps - Plot configuration
* @param {Object} gradient - Color gradient mapping
* @returns {string} PNG data URL of generated scale image
* 
* @example
* <Scale
*   plotProps={{
*     label: "SPL",
*     units: "dB",
*     minLevel: 60,
*     maxLevel: 90
*   }}
*   gradient={{
*     60: [255,255,255],
*     90: [0,0,255]
*   }}
* />
* 
*/
const getImage = (plotProps, gradient) => {
  const { width, height } = styles.image;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  //set dimensions
  canvas.width = width;
  canvas.height = height;
  // Fill it
  context.clearRect(0, 0, width, height);
  for (var x = 0; x <= width; x++) {
    context.strokeStyle = `rgba(${gradient[range2Value(plotProps, width, x)]})`
    context.beginPath();
    context.moveTo(x, 0);
    context.lineTo(x, height);
    context.stroke();
  };
  // Add labels 
  context.fillStyle = "rgba(45, 47, 56 , .8)";
  context.font = "normal 9px arial";
  context.textAlign = "center";
  context.textBaseline = "middle";
  const labels = getLabels(plotProps);
  toPairs(labels).forEach(([key, value]) =>
    context.fillText(key, value2Range(plotProps, width, value), 5.5));
  return  canvas.toDataURL("image/png");
};

/**
* @function Scale
* @description Renders a color scale legend with labels for PDF plot visualizations.
* Creates gradient bar with value labels and units.
* 
* @param {Object} props
* @param {Object} props.plotProps - Plot configuration properties
* @param {string} props.plotProps.label - Scale label text
* @param {string} props.plotProps.units - Units label text  
* @param {number} props.plotProps.minLevel - Minimum scale value
* @param {number} props.plotProps.maxLevel - Maximum scale value
* @param {Object} props.gradient - Color gradient mapping for scale values
* 
* @description
* Generates scale visualization by:
* 1. Creating canvas element for gradient:
*    - Draws vertical gradient lines
*    - Maps values to colors using gradient
*    - Adds value labels at intervals
* 2. Converting canvas to PNG image
* 3. Rendering in PDF with:
*    - Label text
*    - Gradient image
*    - Units text
* 
* @note
* - Uses @react-pdf/renderer for PDF components
* - Creates temporary canvas for gradient generation
* - Converts canvas to PNG for PDF compatibility  
* - Scale width fixed at 350 units
* - Label font size fixed at 9px Arial
* - Centers scale in container with margins
*/
const Scale = ({ plotProps, gradient }) => {
  const { label, units } = plotProps;
  const image = getImage(plotProps, gradient);
  return (
    <View style={styles.scale} >
      <Text style={styles.label} >{`${label}: `}</Text>
      <Image style={styles.image} src={image} />
      <Text style={styles.label} >{` ${units}`}</Text>
    </View>
  )
};

export default Scale;

